<template>
  <v-dialog
    :model-value="true"
    scrollable
    persistent
    max-width="1760px"
  >
    <v-card
      :loading="loading || isProcessing"
    >
      <v-card-title>
        <span>{{ modalTitle }}</span>

        <v-spacer />

        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <div
        v-if="loading"
        class="d-flex flex-column align-center py-4 px-4"
      >
        <h3 class="mb-6 mt-2">
          Loading A/B Test Details...
        </h3>
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </div>
      <template v-else>
        <v-card-text style="max-width: 1536px; margin: 0 auto;">
          <v-container justify="space-around">
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  :value="bucketName"
                  label="A/B Bucket"
                  disabled
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model.trim="formData.test_name"
                  required
                  label="A/B Test name"
                  :error-messages="testNameErrors"
                  @blur="v$.formData.test_name.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="formData.champion_strategy_id"
                  :items="lendingStrategies"
                  item-title="name"
                  item-value="id"
                  label="Champion Strategy"
                  :error-messages="bestLendingStrategyErrors"
                  @blur="v$.formData.champion_strategy_id.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-checkbox
                  v-model="formData.is_active"
                  label="Enable A/B Test"
                  inset
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-checkbox
                  v-model="formData.cap_enabled"
                  label="Enable Capping ?"
                  persistent-hint
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="formData.cap_unit"
                  :items="capUnitTypes"
                  item-title="text"
                  item-value="value"
                  :disabled="disableCapInputs"
                  label="Cap Unit"
                  :error-messages="capUnitErrors"
                  @blur="v$.formData.cap_unit.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model.number="formData.cap_value"
                  type="number"
                  label="Cap Value"
                  :disabled="disableCapInputs"
                  :error-messages="capValueErrors"
                  @blur="v$.formData.cap_value.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model.number="formData.cap_frequency_days"
                  type="number"
                  :disabled="disableCapInputs"
                  label="Cap Frequency (Days)"
                  :error-messages="capFrequencyDaysErrors"
                  @blur="v$.formData.cap_frequency_days.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-menu
                  v-model="capStartDateOpen"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ props }">
                    <v-text-field
                      :value="formatDate(formData.cap_start_date)"
                      label="Cap start date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="props"
                      :error-messages="capStartDateErrors"
                    />
                  </template>
                  <v-date-picker
                    v-model="formData.cap_start_date"
                    @input="capStartDateOpen = false"
                    @change="v$.formData.cap_start_date.$touch()"
                  />
                </v-menu>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-textarea
                  v-model.trim="formData.description"
                  label="Description"
                  auto-grow
                  rows="3"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <AbTestConditionList
                  v-model="formData.selection_conditions"
                  :lead-columns="leadColumns"
                  :lead-colum-input-defs="leadColumInputDefs"
                  :comparison-operators="comparisonOperators"
                  :server-errors="serverErrors"
                />
              </v-col>
              <v-col cols="12">
                <AbTestTrafficAllocations
                  v-model="formData.strategy_allocations"
                  :champion-strategy="championStrategy"
                  :server-errors="serverErrors"
                  :ab-test-id="formData.id"
                  @valid-traffic-allocations="validTrafficAllocations = $event"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="d-flex align-center justify-space-between">
          <v-alert
            v-if="generalErrorMessage"
            class="flex-grow-1"
            text
            outlined
            type="error"
          >
            {{ generalErrorMessage }}
          </v-alert>

          <v-spacer />

          <v-btn
            class="mr-6 btn-bg-deep-blue"
            :disabled="loading || isProcessing"
            @click="handleSave"
          >
            Save changes
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, requiredIf } from '@vuelidate/validators';
import AbTestConditionList from './AbTestConditionList.vue';
import AbTestTrafficAllocations from './AbTestTrafficAllocations.vue';

export default {
  name: 'AbTestDetails',

  components: {
    AbTestConditionList,
    AbTestTrafficAllocations,
  },

  props: {
    abTestId: {
      type: Number,
      default: null,
    },

    bucketName: {
      type: String,
      default: '',
    },

    bucketId: {
      type: Number,
      required: true,
    },
  },

  emits: ['saved', 'close'],
  setup() { return { v$: useVuelidate() }; },

  validations() {
    return {
      formData: {
        test_name: { required },
        champion_strategy_id: { required },

        cap_unit: {
          required: requiredIf(() => this.formData.cap_enabled),
        },
        cap_value: {
          required: requiredIf(() => this.formData.cap_enabled),
          minValue: minValue(0),
        },
        cap_frequency_days: {
          required: requiredIf(() => this.formData.cap_enabled),
          minValue: minValue(0),
        },
        cap_start_date: {
          required: requiredIf(() => this.formData.cap_enabled),
        },
      },
    };
  },

  data() {
    return {
      capStartDateOpen: false,

      formData: {
        id: this.abTestId || null,
        test_name: '',
        description: '',
        cap_enabled: false,
        cap_unit: '',
        cap_value: 0,
        cap_frequency_days: 0,
        cap_start_date: null,
        selection_conditions: [],
        strategy_allocations: [],
        is_active: false,
        champion_strategy_id: null,
        created_at: '',
        updated_at: '',
      },

      capUnitTypes: [
        { text: 'Funded Leads', value: 'funded_leads' },
        { text: 'Funded Amount', value: 'funded_amount' },
        { text: 'Accepted Lead', value: 'accepted_lead' },
      ],

      leadColumns: [],
      leadColumInputDefs: {},
      comparisonOperators: [
        { value: '==', text: 'Equal' },
        { value: '!=', text: 'Not equal' },
        { value: '<', text: 'Less than' },
        { value: '>', text: 'Greater than' },
        { value: '<=', text: 'Less than or equal to' },
        { value: '>=', text: 'Greater than or equal to' },
      ],
      lendingStrategies: [],

      loading: false,
      isProcessing: false,

      activeTab: '0',

      validTrafficAllocations: true,

      serverErrors: {},

      generalErrorMessage: null,
    };
  },

  computed: {
    isEditMode() {
      return !!this.formData?.id;
    },

    championStrategy() {
      const id = this.formData.champion_strategy_id;
      return this.lendingStrategies.find(
        // eslint-disable-next-line eqeqeq -- API may return string ids; strategies use numeric ids
        (strategy) => strategy.id == id,
      );
    },

    disableCapInputs() {
      return !this.formData.cap_enabled;
    },

    modalTitle() {
      if (this.isEditMode) {
        return `Edit ${this.bucketName} A/B Test: ${this.formData.test_name}`;
      }

      return `Create New ${this.bucketName} A/B Test`;
    },

    testNameErrors() {
      return this.getFieldErrors('test_name', {
        required: 'A/B Test name is required.',
      });
    },

    bestLendingStrategyErrors() {
      return this.getFieldErrors('champion_strategy_id', {
        required: 'Champion Strategy is required.',
      });
    },

    capUnitErrors() {
      return this.getFieldErrors('cap_unit', {
        required: 'Cap unit is required.',
      });
    },

    capValueErrors() {
      return this.getFieldErrors('cap_value', {
        required: 'Cap value is required.',
        minValue: 'Cap value must be greater or equal to 0.',
      });
    },

    capFrequencyDaysErrors() {
      return this.getFieldErrors('cap_frequency_days', {
        required: 'Cap frequency days is required.',
        minValue: 'Cap frequency days must be greater or equal to 0.',
      });
    },

    capStartDateErrors() {
      return this.getFieldErrors('cap_start_date', {
        required: 'Cap start date is required.',
      });
    },

    totalAllocation() {
      return this.formData
        ?.strategy_allocations
        .reduce((sum, allocation) => sum + (Number(allocation.allocationPct) || 0), 0);
    },
  },

  watch: {
    abTestId: {
      async handler(newVal) {
        await this.fetchFormData(newVal);
      },
      immediate: true,
    },

    'formData.cap_enabled': {
      handler(newVal) {
        if (!newVal) {
          this.formData.cap_unit = null;
          this.formData.cap_value = null;
          this.formData.cap_frequency_days = null;
          this.formData.cap_start_date = null;
        }
      },
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    getFieldErrors(fieldName, errorMessages) {
      const field = this.v$.formData[fieldName];

      // Return server errors first if they exist
      if (this.serverErrors?.[fieldName]) {
        return this.serverErrors[fieldName];
      }

      // Return empty array if field hasn't been touched
      if (!field?.$dirty) {
        return [];
      }

      // Collect validation errors
      return Object.entries(errorMessages)
        .filter(([validationType]) => !field[validationType])
        .map(([, message]) => message);
    },

    handleSave() {
      this.generalErrorMessage = null;

      this.v$.$touch();

      if (this.v$.$invalid) {
        this.generalErrorMessage = 'Please correct the errors in the form and resubmit it.';
        return;
      }

      if (!this.validTrafficAllocations) {
        this.generalErrorMessage = 'Please correct the errors in the Strategy Traffic Allocations tab and resubmit.';
        return;
      }

      if (this.totalAllocation !== 100) {
        this.generalErrorMessage = `
          Strategy traffic allocations must total 100%.
          The current total is ${this.totalAllocation}%.
        `;
        return;
      }

      this.saveAbTest({
        ...this.formData,
        bucket_id: this.bucketId,
      });
    },

    async saveAbTest(payload) {
      try {
        this.serverErrors = {};
        this.isProcessing = true;

        const { data } = this.isEditMode
          ? await this.$axios.put(`api/v1/ab-tests/${this.formData.id}`, payload)
          : await this.$axios.post('api/v1/ab-tests', payload);

        this.formData = { ...this.formData, ...data?.data };

        this.setNotification({
          color: 'green',
          message: 'A/B Test saved successfully',
        });

        this.$emit('saved', { ...data?.data });
      } catch (error) {
        const status = error?.response?.status || 500;

        if (status === 422) {
          this.serverErrors = error?.response?.data?.errors || {};
          this.generalErrorMessage = 'Please correct the errors in the form and resubmit it.';
        } else {
          this.setNotification({
            color: 'red',
            message: 'An error occurred while saving the A/B Test.',
          });
        }
      } finally {
        this.isProcessing = false;
      }
    },

    async fetchFormData(abTestId = null) {
      try {
        this.loading = true;

        const { data } = await this.$axios.get('api/v1/ab-tests/form-data', {
          params: {
            ab_test_id: abTestId,
          },
        });

        this.formData = {
          ...this.formData,
          ...data?.data,
        };

        this.leadColumns = data?.lead_columns || [];
        this.leadColumInputDefs = data?.lead_colum_input_defs || [];
        this.lendingStrategies = data?.lending_strategies || [];

        // this.comparisonOperators = data?.comparison_operators || [];
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Error fetching ab-test details',
        });
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },

    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    },
  },
};
</script>
